import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../App";
import ClientPortalSectionShell from "../components/clientPortal/ClientPortalSectionShell";
import ClientRealEstateNoAccess from "../components/clientPortal/ClientRealEstateNoAccess";
import { CLIENT_PORTAL_PRIMARY_NAV } from "../clientPortal/clientPortalPrimaryNav";
import { hasRealEstatePortalEntitlement } from "../utils/realEstatePortalAccess";

/**
 * Real estate workspace: primary portal rail + outlet.
 * CL-325: soft page backdrop + realEstate shell presentation (account/resources unchanged).
 * CL-334: calmer editorial backdrop (less saturated slab).
 * CL-337L: gate entire RE subtree unless user has realEstate service / realEstatePortalAccess.
 */
export default function ClientRealEstateLayout() {
  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u || u.isAnonymous) {
        setProfile(null);
        setAuthReady(true);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        setProfile(snap.exists() ? snap.data() : null);
      } catch {
        setProfile(null);
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  const u = auth.currentUser;
  const signedInNonGuest = !!(u && !u.isAnonymous);
  const entitled = signedInNonGuest && hasRealEstatePortalEntitlement(profile);

  if (!authReady) {
    return (
      <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-stone-50 via-neutral-50 to-stone-100/90">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center text-sm text-slate-600">Loading…</div>
      </div>
    );
  }

  if (signedInNonGuest && !entitled) {
    return <ClientRealEstateNoAccess profile={profile} />;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-stone-50 via-neutral-50 to-stone-100/90">
      <ClientPortalSectionShell
        navLabel="Portal navigation"
        items={CLIENT_PORTAL_PRIMARY_NAV}
        presentation="realEstate"
      >
        <Outlet />
      </ClientPortalSectionShell>
    </div>
  );
}
